import { isNil } from 'lodash';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Frequency, RRule, Options as RRuleOptions } from 'rrule';
import { Batch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext/BatchContext';
import { i18n } from '@shared/util/i18n';
import { buildCustomRRule } from '../utils';

interface UseCustomBatchReturn {
  freq: Frequency;
  frequencyLabel: string;
  setFreq: Dispatch<SetStateAction<Frequency>>;
  freqOptions: { title: string; onPress: () => void; frequency: Frequency }[];
  interval: number | undefined;
  setInterval: Dispatch<SetStateAction<number | undefined>>;
  setCustomRrule: (newOptions: Partial<RRuleOptions>) => void;
  handleIntervalChange: (newInterval: string) => void;
  setUserIsEditing: Dispatch<SetStateAction<boolean>>;
}

export function useCustomBatch({
  setRruleOptions,
  rruleOptions,
  setUserIsEditing,
}: Pick<Batch, 'setRruleOptions' | 'rruleOptions' | 'setUserIsEditing'>): UseCustomBatchReturn {
  const [freq, setFreq] = useState(rruleOptions?.freq ?? RRule.WEEKLY);
  const [interval, setInterval] = useState<number | undefined>(rruleOptions?.interval ?? 1);
  const dtstartDate = useMemo(() => {
    if (rruleOptions?.dtstart) {
      return new Date(rruleOptions.dtstart);
    }
    return new Date();
  }, [rruleOptions]);
  const defaultMonth = dtstartDate.getUTCMonth() + 1; // RRule months are 1-indexed
  const defaultDay = dtstartDate.getUTCDate();

  const setCustomRrule = (newOptions: Partial<RRuleOptions>) => {
    setRruleOptions(prev => buildCustomRRule(prev, newOptions));
  };

  const handleIntervalChange = (newInterval: string) => {
    if (newInterval === '') {
      setInterval(undefined);
      setCustomRrule({ interval: 1 });
      return;
    }
    const intervalCount = Number(newInterval);
    const isNumber = !isNaN(intervalCount);
    if (isNumber && intervalCount > 0) {
      setInterval(intervalCount);
      setCustomRrule({ interval: intervalCount });
    }
    setUserIsEditing(true);
  };

  const freqOptions = [
    {
      title: getFrequencyLabel(RRule.HOURLY, interval ?? 0),
      frequency: RRule.HOURLY,
      onPress: () => {
        setFreq(RRule.HOURLY);
        setCustomRrule({ freq: RRule.HOURLY });
      },
    },
    {
      title: getFrequencyLabel(RRule.DAILY, interval ?? 0),
      frequency: RRule.DAILY,
      onPress: () => {
        setFreq(RRule.DAILY);
        setCustomRrule({ freq: RRule.DAILY });
      },
    },
    {
      title: getFrequencyLabel(RRule.WEEKLY, interval ?? 0),
      frequency: RRule.WEEKLY,
      onPress: () => {
        setFreq(RRule.WEEKLY);
        setCustomRrule({ freq: RRule.WEEKLY });
      },
    },
    {
      title: getFrequencyLabel(RRule.MONTHLY, interval ?? 0),
      frequency: RRule.MONTHLY,
      onPress: () => {
        setFreq(RRule.MONTHLY);
        setCustomRrule({ freq: RRule.MONTHLY });
      },
    },
    {
      title: getFrequencyLabel(RRule.YEARLY, interval ?? 0),
      frequency: RRule.YEARLY,
      onPress: () => {
        setFreq(RRule.YEARLY);
        setCustomRrule({
          freq: RRule.YEARLY,
          bymonthday: !isNil(rruleOptions?.bymonthday) ? rruleOptions?.bymonthday : [defaultDay],
          bymonth: !isNil(rruleOptions?.bymonth) ? rruleOptions?.bymonth : [defaultMonth],
        });
      },
    },
  ];

  return {
    freq,
    frequencyLabel: getFrequencyLabel(freq, interval ?? 0),
    setFreq,
    freqOptions,
    interval,
    setInterval,
    setCustomRrule,
    handleIntervalChange,
    setUserIsEditing,
  };
}

function getFrequencyLabel(freq: Frequency, interval: number) {
  if (freq === RRule.HOURLY) {
    return i18n('workflowTemplate.component.dateAndRepeat.frequencyWithCount.hour', {
      count: interval,
    });
  }
  if (freq === RRule.DAILY) {
    return i18n('workflowTemplate.component.dateAndRepeat.frequencyWithCount.day', {
      count: interval,
    });
  }
  if (freq === RRule.WEEKLY) {
    return i18n('workflowTemplate.component.dateAndRepeat.frequencyWithCount.week', {
      count: interval,
    });
  }
  if (freq === RRule.MONTHLY) {
    return i18n('workflowTemplate.component.dateAndRepeat.frequencyWithCount.month', {
      count: interval,
    });
  }
  return i18n('workflowTemplate.component.dateAndRepeat.frequencyWithCount.year', {
    count: interval,
  });
}
